import { t, Trans } from '@lingui/macro';
import hotkeys from 'hotkeys-js';
import { ChevronDown, ChevronUp, Mic, MicOff, MonitorOff, MonitorUp, PhoneOff, Users, Volume2 } from 'lucide-react';
import { useEffect, useReducer, useState } from 'react';
import { Room } from '../../models/Room';
import { My } from '../../services/My';
import { twil, TwilioHelpEvent } from '../../services/TwilioHelper';
import { Util } from '../../services/Utils/Util';
import { UtilApp } from '../../services/Utils/UtilApp';
import { ParticipantItem } from './CallInfoItems';
import useDefaultDevices from './useDefaultDevices';

import { Badge } from "../../components/ui/badge";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { Button } from "../../components/ui/button";
import { useToast } from "../../components/ui/use-toast";
import { cn } from "../../lib/utils";

hotkeys.unbind();

const CallHeader: React.FC = () => {
  const [_, forceUpdate] = useReducer((x) => x + 1, 0);
  const [expanded, setExpanded] = useState(false);
  const [roomName, setRoomName] = useState('');
  const [isOneToOne, setIsOneToOne] = useState(false);
  const { toast } = useToast();
  const { audioInputDevice, audioOutputDevice } = useDefaultDevices();
  const inputLabel = Util.getTextInsideParenthesis(audioInputDevice?.label);

  const [alertConfig, setAlertConfig] = useState<{
    open: boolean;
    title: React.ReactNode;
    description: React.ReactNode;
    onAction?: () => void;
  }>({ open: false, title: '', description: '' });

  useEffect(() => {
    let unsub1 = twil.subscribeToTwilioUpdates(twilioStateChangeHandler)
    window.addEventListener('beforeunload', handleBeforeUnload);

    hotkeys('alt+a', function (event, handler) {
      event.preventDefault()
      if (twil.isConnected()) {
        toggleMic(!twil.isMicEnabled());
      }
    });

    hotkeys('alt+s', function (event, handler) {
      event.preventDefault()
      if (twil.isConnected()) {
        toggleScreenSharing();
      }
    });

    return () => { unsub1(); twil.leaveRoom(); window.removeEventListener('beforeunload', handleBeforeUnload); hotkeys.unbind('alt+a'); hotkeys.unbind('alt+s'); };
  }, [])

  useEffect(() => {
    if (twil.audioInputLabel && inputLabel && inputLabel !== twil.audioInputLabel) {
      showAlert(
        t`Reconnect to the call`,
        t`You changed your microphone to ${inputLabel}. Reconnect to apply this change.`,
        () => { } // Just OK
      );
    }
  }, [inputLabel])

  function showAlert(title: React.ReactNode, description: React.ReactNode, onAction?: () => void) {
    setAlertConfig({ open: true, title, description, onAction });
  }

  function twilioStateChangeHandler(e: TwilioHelpEvent | null) {
    if (e?.type === 'StateChange') {
      if (twil.isConnected()) {
        let room = My.Rooms.get(twil.getRoomId());
        if (room) {
          setRoomName(Room.getRoomName(room, My.user.id));
          setIsOneToOne(room.isOneToOne)
        }
      } else {
        setExpanded(false);
      }

      if (e?.error) {
        // e.error has header and message possibly? Or just message.
        // Assuming e.error is { header, message } based on previous usage present(e.error) where previous useIonAlert took hook args.
        // Or e.error could be just string or object.
        // Looking at previous code: present(e.error).
        // If e.error matches IonAlertOptions.
        const header = (e.error as any).header || 'Error';
        const message = (e.error as any).message || JSON.stringify(e.error);
        showAlert(header, message);
      }
      forceUpdate();
    }
  }

  async function handleBeforeUnload(e: BeforeUnloadEvent) {
    if (twil.isConnected()) {
      e.preventDefault();
      console.log('### beforeunload handled');
      twil.leaveRoom();
      e.returnValue = "Are you sure you want to leave?";
    }
  }

  return (
    <>
      <div className="fixed top-0 left-0 right-0 z-50 pointer-events-none flex flex-col items-center">
        {twil.isConnecting && (
          <div className="w-full h-1 bg-primary/20">
            <div className="h-full bg-primary w-1/3 animate-indeterminate-slide rounded-full" />
          </div>
        )}

        {twil.isConnected() && (
          <div className={cn(
            "pointer-events-auto transition-all duration-300 ease-in-out shadow-lg mx-4 mt-2",
            "bg-slate-900/95 backdrop-blur-md text-white rounded-2xl overflow-hidden border border-slate-700/50",
            expanded ? "max-w-2xl w-full" : "max-w-fit"
          )}>
            {/* Header / Toggle Row */}
            <div
              className="flex items-center justify-between px-4 py-2 gap-4 cursor-pointer hover:bg-white/5 transition-colors"
              onClick={() => setExpanded(p => !p)}
            >
              <div className="flex items-center gap-2">
                {expanded ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                <div className="flex items-center gap-3 ml-1">
                  <span className="font-semibold text-lg max-w-[150px] sm:max-w-[200px] truncate leading-none" title={roomName}>
                    #{roomName}
                  </span>
                  <Badge variant="secondary" className="flex items-center gap-1.5 px-2 py-0.5 bg-white/10 text-white hover:bg-white/20 border-0 h-6 shrink-0">
                    <Users className="w-3 h-3" />
                    <span className="text-xs font-medium tabular-nums">{twil.participantSpans.size}</span>
                  </Badge>
                </div>
              </div>

              {/* Quick Actions (Always visible) */}
              <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                {UtilApp.getPlatform() === 'web' && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-white hover:bg-white/20 hover:text-white"
                    onClick={toggleScreenSharing}
                    title={twil.isScreenSharing() ? t`Stop sharing` : t`Share screen`}
                  >
                    {twil.isScreenSharing() ? <MonitorOff className="w-5 h-5" /> : <MonitorUp className="w-5 h-5" />}
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-white hover:bg-white/20 hover:text-white"
                  onClick={() => toggleMic(!twil.isMicEnabled())}
                  title={twil.isMicEnabled() ? t`Mute` : t`Unmute`}
                >
                  {twil.isMicEnabled() ? <Mic className="w-5 h-5" /> : <MicOff className="w-5 h-5 text-red-400" />}
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-red-500 hover:bg-red-500/20 hover:text-red-500"
                  onClick={() => twil.leaveRoom()}
                  data-cy="close-call"
                  title={t`Leave call`}
                >
                  <PhoneOff className="w-5 h-5" />
                </Button>
              </div>
            </div>

            {/* Expanded Content */}
            {expanded && (
              <div className="px-4 pb-4 pt-2 border-t border-white/10 space-y-4">
                {/* Participants */}
                <div className="flex flex-wrap gap-2">
                  {Array.from(twil.participantSpans.values()).map((span) => (
                    <div key={span.id} className="bg-white/10 rounded px-2 py-1">
                      <ParticipantItem html={span} />
                    </div>
                  ))}
                </div>

                {/* Screen Sharing Info */}
                {twil.isScreenSharing() && (
                  <div className="flex items-center justify-between bg-blue-500/20 rounded-lg p-3 border border-blue-500/30">
                    <div className="flex items-center gap-2 text-sm">
                      <MonitorUp className="w-4 h-4 text-blue-400" />
                      <span><Trans>You are now presenting your screen.</Trans></span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={toggleScreenSharing}
                      className="text-red-300 hover:text-red-200 hover:bg-red-500/20 h-8"
                    >
                      <Trans>Stop screensharing</Trans>
                    </Button>
                  </div>
                )}

                {/* Device Info */}
                <div className="flex flex-col gap-1 text-xs text-gray-400">
                  {inputLabel && (
                    <div className="flex items-center gap-2">
                      <Mic className="w-3 h-3" />
                      <span>{inputLabel}</span>
                    </div>
                  )}
                  {audioOutputDevice && (
                    <div className="flex items-center gap-2">
                      <Volume2 className="w-3 h-3" />
                      <span>{audioOutputDevice?.label.replace('Default - ', '')}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <AlertDialog open={alertConfig.open} onOpenChange={(open) => setAlertConfig(p => ({ ...p, open }))}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{alertConfig.title}</AlertDialogTitle>
            <AlertDialogDescription>{alertConfig.description}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => {
              if (alertConfig.onAction) alertConfig.onAction();
              setAlertConfig(p => ({ ...p, open: false }));
            }}>
              {t`OK`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );


  async function toggleScreenSharing() {
    if (!twil.isScreenSharing()) {
      let res = await twil.startScreenSharing(forceUpdate);
      if (res == 'busy') {
        showAlert(t`Screen Sharing`, t`Someone is already sharing the screen. They must stop sharing before you can start sharing your screen.`);
      } else if (res == 'failed') {
        showAlert(t`Error`, t`Failed to share your screen.`);
      } else if (res == 'started') {
        toastText(t`Sharing your screen (keyboard shortcut: Alt + s)`);
      }
    } else {
      twil.stopScreenSharing();
    }
    forceUpdate();
  };


  function toggleMic(value: boolean) {
    twil.enableLocalMicrophone(value);
    let msg = value ? t`Microphone is now enabled` : t`Microphone is now disabled`;
    if (UtilApp.getPlatform() === 'web') {
      msg += ' ' + t`(keyboard shortcut: Alt + a)`;
    }
    toastText(msg);
  }

  function toastText(text: string) {
    toast({
      description: text,
      duration: 1000,
    });
  }
}

export default CallHeader;


